import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import ConnectProBrand, { connectProGame } from "./ConnectProBrand";
import { AlreadyPlayedPage } from "@precisionpros/game-ui";

const hubUrl = () => (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') ? 'http://localhost:3000' : 'https://games.precisionpros.com';

const API_URL = process.env.REACT_APP_API_URL;

const StartScreen = () => {
  const [name, setName] = useState("");
  const [mode, setMode] = useState("hard"); // Default to hard
  const [isReturning, setIsReturning] = useState(false);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [hasPlayedToday, setHasPlayedToday] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramName = params.get("player_name");
    const savedName = paramName || localStorage.getItem("playerName");

    if (savedName) {
      setName(savedName);
      setIsReturning(true);
      if (paramName) {
        localStorage.setItem("playerName", paramName);
      }
      checkIfAlreadyPlayed(savedName);
    }
  }, []);

  const checkIfReserved = async (playerName) => {
    try {
      const response = await fetch(`${API_URL}/verify-name/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_name: playerName, password: "invalid-check" }),
      });
      return response.status !== 404;
    } catch {
      return false;
    }
  };

  const verifyPassword = async () => {
    try {
      const response = await fetch(`${API_URL}/verify-name/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_name: name.trim(), password })
      });
      const data = await response.json();
      return response.ok && data.success === true;
    } catch {
      return false;
    }
  };

  const checkIfAlreadyPlayed = async (playerName) => {
    try {
      const response = await fetch(`${API_URL}/has-played-today/?player_name=${encodeURIComponent(playerName)}`);
      if (response.ok) {
        const data = await response.json();
        setHasPlayedToday(data.has_played_today === true);
      }
    } catch {
      // Fail silently
    }
  };

  const handleStart = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      alert("Please enter your name.");
      return;
    }
    if (trimmedName.length > 15) {
      alert("Name must be 15 characters or fewer.");
      return;
    }

    const reserved = await checkIfReserved(trimmedName);
    const remembered = localStorage.getItem("reservedName");
    if (reserved && remembered !== trimmedName && !passwordRequired) {
      setPasswordRequired(true);
      return;
    }

    if (reserved) {
      const verified = await verifyPassword();
      if (verified) localStorage.setItem("reservedName", trimmedName);
      if (!verified) {
        setPasswordError("Incorrect password. Please try again.");
        return;
      }
    }

    localStorage.setItem("playerName", trimmedName);
    setPasswordError("");

    navigate("/game", {
      state: {
        playerName: trimmedName,
        gameMode: mode,
      },
    });
  };

  if (hasPlayedToday && isReturning) {
    return <AlreadyPlayedPage
      game={connectProGame}
      title="You already played today!"
      message="Come back tomorrow for a new puzzle."
      leaderboardHref="/leaderboard"
      homeHref={hubUrl()}
    />;
  }

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#fafbfc", padding: "20px 12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ maxWidth: "500px", width: "100%", backgroundColor: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08), 0 8px 24px rgba(0, 0, 0, 0.06)", padding: "32px 24px" }}>
        <ConnectProBrand size="login" className="connectpro-login-brand" />

        <p style={{ fontSize: "0.95rem", color: "#555", marginBottom: "24px", lineHeight: "1.5", marginTop: "0" }}>
          {isReturning ? (
            <>👋 Welcome back, <strong>{name}</strong>! Ready to play ConnectPro?</>
          ) : (
            <>A daily word-chain puzzle game.</>
          )}
        </p>
        <input
          type="text"
          placeholder="Enter your name"
          value={name}
          maxLength={15}
          onChange={(e) => setName(e.target.value)}
          style={{ width: "100%", padding: "12px 14px", fontSize: "16px", borderRadius: "8px", border: "1px solid #d3d6da", boxSizing: "border-box", marginBottom: "12px" }}
        />

        {passwordRequired && (
          <div style={{ marginBottom: "12px" }}>
            <input
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: "100%", padding: "12px 14px", fontSize: "16px", borderRadius: "8px", border: "1px solid #d3d6da", boxSizing: "border-box", marginBottom: "8px" }}
            />
            {passwordError && (
              <p style={{ color: "#dc2626", marginTop: "0", marginBottom: "0", fontSize: "0.85rem" }}>{passwordError}</p>
            )}
          </div>
        )}

        {hasPlayedToday && (
          <div style={{ marginBottom: "16px", padding: "12px", backgroundColor: "#f0fdf4", borderRadius: "8px", border: "1px solid #16a34a" }}>
            <p style={{ fontSize: "0.9rem", color: "#15803d", margin: "0", fontWeight: "500" }}>
              ✅ You already played today. Come back tomorrow!
            </p>
          </div>
        )}

        <div style={{ marginBottom: "20px" }}>
          <p style={{ fontSize: "0.85rem", fontWeight: "600", color: "#666", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "12px" }}>
            Select Difficulty
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {[
              { value: "easy", label: "Easy (no timer)" },
              { value: "medium", label: "Medium (timed)" },
              { value: "hard", label: "Hard (timed, strict mode)" }
            ].map(option => (
              <label key={option.value} style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer", padding: "10px", borderRadius: "8px", backgroundColor: mode === option.value ? "#eff6ff" : "transparent", transition: "all 0.15s ease" }}>
                <input
                  type="radio"
                  value={option.value}
                  checked={mode === option.value}
                  onChange={() => setMode(option.value)}
                  style={{ cursor: "pointer", width: "18px", height: "18px" }}
                />
                <span style={{ fontSize: "0.95rem", color: "#333", fontWeight: "500" }}>{option.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <button
            onClick={handleStart}
            disabled={hasPlayedToday}
            style={{
              flex: 1,
              padding: "14px 16px",
              fontSize: "1rem",
              fontWeight: "800",
              backgroundColor: hasPlayedToday ? "#9ca3af" : "#2563eb",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: hasPlayedToday ? "not-allowed" : "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              transition: "all 0.2s ease",
              boxShadow: hasPlayedToday ? "none" : "0 2px 0 #1d4ed8",
              opacity: hasPlayedToday ? 0.6 : 1
            }}
            onMouseEnter={(e) => !hasPlayedToday && (e.target.style.filter = "brightness(1.05)")}
            onMouseLeave={(e) => !hasPlayedToday && (e.target.style.filter = "none")}
          >
            Start Game
          </button>
          <Link
            to="/leaderboard"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "14px 16px",
              fontSize: "1rem",
              fontWeight: "800",
              backgroundColor: "#f3f4f6",
              color: "#333",
              textDecoration: "none",
              border: "1.5px solid #d3d6da",
              borderRadius: "8px",
              cursor: "pointer",
              textTransform: "uppercase",
              letterSpacing: "0.6px",
              transition: "all 0.2s ease"
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#e5e7eb";
              e.target.style.borderColor = "#c4c7ce";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#f3f4f6";
              e.target.style.borderColor = "#d3d6da";
            }}
          >
            🏆 Leaderboard
          </Link>
        </div>

        <Link
          to="/how-to-play"
          style={{
            display: "inline-block",
            fontSize: "0.9rem",
            color: "#2563eb",
            textDecoration: "none",
            fontWeight: "600",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => e.target.style.color = "#1d4ed8"}
          onMouseLeave={(e) => e.target.style.color = "#2563eb"}
        >
          How to Play →
        </Link>
      </div>
    </div>
  );
};

export default StartScreen;
